import {type SyntheticEvent, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {redeemPromo} from '../../../common/api/generated/endpoints/credits/credits'
import type {UserResponse} from '../../../common/api/generated/model/userResponse'
import {isApiError} from '../../../common/errors/ApiError'
import {useToast} from '../../../common/components/toast/toastContext'
import {trackEvent} from '../../../common/analytics/trackEvent'

interface Props {
  me: UserResponse
  setMe: (me: UserResponse) => void
  /** Fired after a successful redeem with the number of credits granted. */
  onRedeemed: (creditsAdded: number) => void
}

const RedeemPromoCodeCard = ({me, setMe, onRedeemed}: Props) => {
  const {t} = useTranslation()
  const toast = useToast()

  const [code, setCode] = useState('')
  const [redeeming, setRedeeming] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault()
    const trimmed = code.trim()
    if (!trimmed || redeeming) return

    setRedeeming(true)
    setError(null)
    trackEvent('account_promo_code_submitted')

    try {
      const res = await redeemPromo({code: trimmed})
      setMe({...me, credits: res.data.credits})
      trackEvent('account_promo_code_redeemed', {credits_awarded: res.data.creditsAdded})
      setCode('')
      onRedeemed(res.data.creditsAdded)
    } catch (err) {
      trackEvent('account_promo_code_failed')
      if (isApiError(err) && err.status === 409) {
        setError(t('account.redeem.errorAlreadyRedeemed'))
      } else if (isApiError(err) && err.status === 400) {
        setError(t('account.redeem.errorInvalid'))
      } else {
        const msg = t('account.redeem.errorGeneric')
        setError(msg)
        if (isApiError(err) && err.status >= 500) toast.error(msg)
      }
    } finally {
      setRedeeming(false)
    }
  }

  return (
    <section className="acc-card">
      <header className="acc-card-head">
        <h2>{t('account.redeem.title')}</h2>
        <p>{t('account.redeem.description')}</p>
      </header>

      <form className="acc-redeem-form" onSubmit={handleSubmit}>
        <input
          className="acc-input acc-redeem-input"
          placeholder={t('account.redeem.placeholder')}
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          aria-label={t('account.redeem.title')}
        />
        <button type="submit" className="acc-btn primary" disabled={!code.trim() || redeeming}>
          {redeeming ? t('account.redeem.redeeming') : t('account.redeem.button')}
        </button>
      </form>

      {error && <div className="acc-error-flag acc-redeem-error">{error}</div>}
    </section>
  )
}

export default RedeemPromoCodeCard
